import { Link } from 'react-router-dom';
import { ArrowUpRight, Compass, Eye, Gem, Route } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { Reveal } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { ServiceCard } from '../components/ServiceCard';
import { services } from '../data/services';
import { img } from '../data/site';
import { usePageMeta } from '../components/ScrollToTop';

const pillars = [
  {
    icon: Compass,
    title: 'Our Mission',
    text: 'To help businesses, organizations, and individuals navigate the digital world with greater clarity, creativity, and purpose — and turn visibility into opportunity.',
  },
  {
    icon: Eye,
    title: 'Our Vision',
    text: 'A world where every great business — no matter its size — has the opportunity to be discovered, understood, and remembered.',
  },
  {
    icon: Gem,
    title: 'Our Values',
    text: 'Strategy before tactics. Creativity with purpose. Honest communication. Data-informed decisions. And a genuine commitment to the people behind every brand.',
  },
  {
    icon: Route,
    title: 'Our Approach',
    text: 'Understand first. Strategize deliberately. Execute beautifully. Optimize continuously. Growth is a process, and we stay for all of it.',
  },
];

export default function About() {
  usePageMeta(
    'About TheBigHub — Your Digital Growth Partner',
    'TheBigHub is a digital marketing and creative solutions brand combining marketing strategy, technology, content, branding, and digital education under one roof.'
  );
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About TheBigHub"
        title="We Bring Powerful Digital Solutions Together."
        copy="TheBigHub is a digital marketing and creative solutions brand helping businesses, organizations, and individuals navigate the digital world with greater clarity, creativity, and purpose."
        image={img('photo-1600880292203-757bb62b4baf')}
        imageAlt="African professionals at TheBigHub collaborating on a client growth project"
      />

      <section className="bg-cream" data-testid="about-story">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <SectionHeading
                chapter="01"
                eyebrow="Our Story"
                title="More Than a Service Provider."
              />
              <Reveal delay={0.15}>
                <div className="prose-tbh mt-6">
                  <p>
                    Through our eight core services, we combine marketing strategy, technology,
                    content, branding, and digital education to help our clients strengthen their
                    online presence and pursue meaningful opportunities.
                  </p>
                  <p>
                    We believe every great business deserves the opportunity to be discovered,
                    understood, and remembered. That is why we are more than a service provider.
                    We are <strong className="text-forest">Your Digital Growth Partner</strong>.
                  </p>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-gold/50" aria-hidden="true" />
              <div className="img-frame aspect-[4/3] shadow-[0_36px_80px_-32px_rgba(0,59,36,0.4)]">
                <img
                  src={img('photo-1497366216548-37526070297c')}
                  alt="A modern, calm creative workspace at TheBigHub"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          <div className="mt-20 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.09} className="h-full">
                  <div data-testid={`about-pillar-${i + 1}`} className="card-lift h-full rounded-2xl border-2 border-forest bg-forest p-7">
                    <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-goldlight">
                      <Icon size={22} strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-white">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-paper" data-testid="about-services">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <SectionHeading
              chapter="02"
              eyebrow="What We Do"
              title="Eight Services. One Dedicated Partner."
            />
            <Reveal delay={0.2}>
              <Link to="/services" data-testid="about-services-link" className="btn-outline-dark shrink-0">
                Explore All Services <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
